import { h, render, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';
import linkstate from 'linkstate';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { printViaIframe } from '../../lib/printHelper';
import { showReminderLabel } from '../../lib/invoiceReminderLabel';
import { isDuettInvoiceCandidate, defaultDuettSendOption } from '../../lib/duettSaleStatus';

import FormFields from '../../components/form/fields';
import DyrejournalAnimalListMini from '../../components/dyrejournal/listAnimalMini';
import DyrejournalPrescriptionListMini from '../../components/dyrejournal/listPrescriptionMini';
import DyrejournalJournalPrint from '../../components/dyrejournal/journalPrint';
import DyrejournalSaleReceipt from '../../components/dyrejournal/saleReceipt';
import DyrejournalSaleReceiptPrint from '../../components/dyrejournal/saleReceipt';

import ImageScroller from '../../components/imagescroller';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

// Default SMS texts for the pay-link SMS. Placeholders are filled by the backend
// (post-sale-sms-reminder.js), which also appends {{payLink}} if the user removes it.
// The reminder wording mirrors DEFAULTS.no in that route / allVisitsTable.js.
const INVOICE_DEFAULT_SMS = 'Hei {{firstname}}. Faktura {{saleNumber}} på {{amount}} kr fra '
    + '{{clinicName}} er klar. Se fakturaen her: {{payLink}}. Vennlig hilsen {{clinicName}}';
const REMINDER_DEFAULT_SMS = 'Hei {{firstname}}. Vi minner om ubetalt faktura {{saleNumber}} på {{amount}} kr fra '
    + '{{clinicName}}. Betal enkelt her: {{payLink}}. Vennlig hilsen {{clinicName}}';

// Same status set the backend SMS route accepts: 2 unpaid, 3 partial, 4 invoice, 5 account.
const SMS_INVOICE_STATUSES = [2, 3, 4, 5];

@observer
class SendInvoice extends Component {
	constructor(props) {
        super(props);
        this.state = {
            to: '',
            subject: '',
            body: '',
            includeJournal: false,
            invoiceError: null,
            smsMessage: '',
            smsSending: false,
            smsSent: false,
            smsError: null,
            fikenSending: false,
            fikenSent: false,
            fikenError: null,
            tripletexSending: false,
            tripletexSent: false,
            tripletexError: null,
            powerOfficeSending: false,
            powerOfficeSent: false,
            powerOfficeError: null,
            powerOfficeMessage: null,
            powerOfficeRetryable: false,
            powerOfficeForceConfirm: false,
            duettSending: false,
            duettSent: false,
            duettError: null,
            duettSendOption: null,
            duettCreditSending: false,
            duettCreditSent: false,
            duettCreditError: null,
            duettCreditConfirm: false,
            invoiceReferenceSections: fields.get('sale', 'Sale invoice references'),
        };
        this.searchTimer = null;
        this.duettCreditConfirmTimer = null;
        this.powerOfficeForceConfirmTimer = null;
    }

    onClickEmailInvoice = async e => {
        const { newSale, callback = () => {} } = this.props;
        const { to, subject, body, includeJournal } = this.state;
        this.setState({
            invoiceSent: false,
            invoiceSending: true,
            invoiceError: null,
        });
        const { saleStore } = this.props.stores;
        const response = await saleStore.emailReminder({ sale: newSale, to, subject, body, includeJournal });
        callback(response);

        if (response?.status === 200) {
            this.setState({
                invoiceSent: true,
                invoiceSending: false,
            });
        } else {
            this.setState({
                invoiceSent: false,
                invoiceSending: false,
                invoiceError: response?.message || 'Sending av e-post feilet.',
            });
        }
    }

    onClickEmailReceipt = async e => {
        const { newSale, callback = () => {} } = this.props;
        const { to, subject, body } = this.state;
        this.setState({
            invoiceSent: false,
            invoiceSending: true,
            invoiceError: null,
        });
        const { saleStore } = this.props.stores;
        const response = await saleStore.emailReceipt(newSale, false, { to, subject, body });
        callback(response);

        if (response?.status === 200) {
            this.setState({
                invoiceSent: true,
                invoiceSending: false,
            });
        } else {
            this.setState({
                invoiceSent: false,
                invoiceSending: false,
                invoiceError: response?.message || 'Sending av e-post feilet.',
            });
        }
    }

    onClickSmsInvoice = async () => {
        const { newSale, callback = () => {} } = this.props;
        const { smsMessage } = this.state;
        const { saleStore } = this.props.stores;
        this.setState({
            smsSending: true,
            smsSent: false,
            smsError: null,
        });
        const message = smsMessage || this.getDefaultSmsMessage();
        const response = await saleStore.smsReminder({ sale: newSale, message });
        callback(response);

        if (response?.status === 200) {
            this.setState({
                smsSending: false,
                smsSent: true,
            });
        } else {
            this.setState({
                smsSending: false,
                smsSent: false,
                smsError: response?.message || 'Sending av SMS feilet.',
            });
        }
    }

    getDefaultSmsMessage = () => {
        const { newSale } = this.props;
        const { userStore } = this.props.stores;
        const { customer } = userStore;
        const dueDate = new Date(newSale.dueDate || newSale.saleDate || new Date());
        if (customer.invoiceDueDays > 0 && !newSale.dueDate) {
            dueDate.setDate(dueDate.getDate() + customer.invoiceDueDays);
        }
        const { days: daysOverDue } = util.dateDiff(dueDate, new Date());
        return showReminderLabel({ daysOverDue, reminders: newSale.reminders })
            ? REMINDER_DEFAULT_SMS
            : INVOICE_DEFAULT_SMS;
    }

    onClickFikenInvoice = async e => {
        const { newSale } = this.props;
        const { fikenStore, saleStore } = this.props.stores;
        this.setState({
            fikenSending: true,
            fikenSent: false,
            fikenError: null,
        });
        try {
            // If invoice already created in Fiken, skip creation and only send
            if (!newSale.fikenLocation) {
                const referencesSaved = await saleStore.saveSale({
                    calledFrom: 'sendInvoice.onClickFikenInvoice',
                    event: e,
                    fieldsToSave: ['invoiceReferenceNumber', 'invoiceReferencePerson'],
                });
                if (!referencesSaved) {
                    this.setState({
                        fikenSending: false,
                        fikenError: 'Kunne ikke lagre fakturareferansene.',
                    });
                    return;
                }
                const createResponse = await fikenStore.createCompanyInvoice(newSale.id);
                if (createResponse.status !== 200) {
                    // Show the backend's reason — a bare status code left the vet
                    // with no idea what to fix (same lesson as TICKET-01362).
                    this.setState({
                        fikenSending: false,
                        fikenError: createResponse.message || `Fiken create failed: ${createResponse.status}`,
                    });
                    return;
                }
                // Auto-send at transfer time (customer.fikenSendInvoiceToCustomer)
                // may already have delivered the invoice — don't send it twice.
                if (createResponse.included?.autoSend?.sendToCustomer) {
                    this.setState({
                        fikenSending: false,
                        fikenSent: true,
                    });
                    return;
                }
            }
            const sendResponse = await fikenStore.sendCompanyInvoice(newSale.id);
            if (sendResponse.status === 200) {
                this.setState({
                    fikenSending: false,
                    fikenSent: true,
                });
            } else {
                this.setState({
                    fikenSending: false,
                    fikenError: sendResponse.message || `Fiken send failed: ${sendResponse.status}`,
                });
            }
        } catch (err) {
            this.setState({
                fikenSending: false,
                fikenError: `Fiken error: ${err.message || err}`,
            });
        }
    }

    onClickTripletexInvoice = async e => {
        const { newSale } = this.props;
        this.setState({
            tripletexSending: true,
            tripletexSent: false,
            tripletexError: null,
        });
        try {
            const response = await util.fetchApi(`/api/integrations/tripletex/invoices/${newSale.id}`, {
                method: 'POST',
            }, {});
            if (response.status === 200) {
                this.setState({
                    tripletexSending: false,
                    tripletexSent: true,
                });
            } else if (response.status === 409 && response.data?.existingInvoice?.invoiceNumber) {
                // The backend transfer guard: the invoice already exists in Tripletex.
                // Shown verbatim ("... Use forceRetransfer=1 to override.") this read as a
                // failure to retry, so vets clicked again and again (TICKET-01597).
                this.setState({
                    tripletexSending: false,
                    tripletexError: `Allerede overført til Tripletex som faktura #${response.data.existingInvoice.invoiceNumber}.`,
                });
            } else {
                this.setState({
                    tripletexSending: false,
                    tripletexError: `Tripletex failed: ${response.status} - ${response.message || ''}`,
                });
            }
        } catch (err) {
            this.setState({
                tripletexSending: false,
                tripletexError: `Tripletex error: ${err.message || err}`,
            });
        }
    }

    // A green "Sendt til PowerOffice" hides what actually landed there: a sales
    // order is not a booked invoice, and an invoice created for a partly paid
    // sale is deliberately not delivered to the owner. Spell it out so nobody
    // goes looking for a faktura that was never meant to exist (TICKET-01431).
    powerOfficeOutcomeText = response => {
        const { workflowType, invoiceCreated, invoiceSentToCustomer } = response?.data || {};
        if (workflowType === 'cashJournal') {
            return 'Ført som kontantsalg i PowerOffice.';
        }
        if (invoiceCreated) {
            return invoiceSentToCustomer
                ? 'Faktura opprettet i PowerOffice og sendt til kunden.'
                : 'Faktura opprettet i PowerOffice, men ikke sendt til kunden. Send den fra PowerOffice.';
        }
        return 'Salgsordre opprettet i PowerOffice. Den må faktureres i PowerOffice for å bli bokført.';
    }

    onClickPowerOfficeInvoice = async e => {
        const { newSale } = this.props;
        this.setState({
            powerOfficeSending: true,
            powerOfficeSent: false,
            powerOfficeError: null,
            powerOfficeMessage: null,
            powerOfficeRetryable: false,
            powerOfficeForceConfirm: false,
        });
        try {
            const response = await util.fetchApi(`/api/integrations/poweroffice/invoices/${newSale.id}`, {
                method: 'POST',
            }, {});
            if (response.status === 200) {
                this.setState({
                    powerOfficeSending: false,
                    powerOfficeSent: true,
                    powerOfficeMessage: this.powerOfficeOutcomeText(response),
                });
            } else {
                // The backend offers forceRetransfer=1 only on the "already
                // transferred" 409 — the intended delete-in-PowerOffice-and-
                // resend flow. Other 409s (transfer in progress, order still
                // exists in PowerOffice) must not get a force button.
                const canForce = response.status === 409
                    && `${response.message || ''}`.includes('forceRetransfer');
                this.setState({
                    powerOfficeSending: false,
                    powerOfficeError: `PowerOffice failed: ${response.status} - ${response.message || ''}`,
                    powerOfficeRetryable: canForce,
                });
            }
        } catch (err) {
            this.setState({
                powerOfficeSending: false,
                powerOfficeError: `PowerOffice error: ${err.message || err}`,
            });
        }
    }

    onClickPowerOfficeForceRetransfer = async e => {
        const { newSale } = this.props;
        const { powerOfficeForceConfirm } = this.state;
        if (!powerOfficeForceConfirm) {
            this.setState({ powerOfficeForceConfirm: true });
            if (this.powerOfficeForceConfirmTimer) {
                clearTimeout(this.powerOfficeForceConfirmTimer);
            }
            this.powerOfficeForceConfirmTimer = setTimeout(() => {
                this.setState({ powerOfficeForceConfirm: false });
            }, 5000);
            return;
        }
        if (this.powerOfficeForceConfirmTimer) {
            clearTimeout(this.powerOfficeForceConfirmTimer);
        }
        this.setState({
            powerOfficeForceConfirm: false,
            powerOfficeSending: true,
            powerOfficeSent: false,
            powerOfficeError: null,
            powerOfficeMessage: null,
        });
        try {
            const response = await util.fetchApi(`/api/integrations/poweroffice/invoices/${newSale.id}?forceRetransfer=1`, {
                method: 'POST',
            }, {});
            if (response.status === 200) {
                this.setState({
                    powerOfficeSending: false,
                    powerOfficeSent: true,
                    powerOfficeRetryable: false,
                    powerOfficeMessage: this.powerOfficeOutcomeText(response),
                });
            } else {
                this.setState({
                    powerOfficeSending: false,
                    powerOfficeError: `PowerOffice failed: ${response.status} - ${response.message || ''}`,
                });
            }
        } catch (err) {
            this.setState({
                powerOfficeSending: false,
                powerOfficeError: `PowerOffice error: ${err.message || err}`,
            });
        }
    }

    onClickDuettInvoice = async e => {
        const { newSale } = this.props;
        const { duettSendOption } = this.state;
        const visitor = newSale.visitors?.[0];
        const invoiceSendOption = duettSendOption || defaultDuettSendOption(visitor);
        this.setState({ duettSending: true, duettSent: false, duettError: null });
        try {
            const response = await util.fetchApi(`/api/integrations/duett/invoices/${newSale.id}`, {
                method: 'POST',
            }, { invoiceSendOption });
            if (response.status === 200) {
                this.setState({ duettSending: false, duettSent: true });
            } else {
                this.setState({ duettSending: false, duettError: `Duett failed: ${response.status} - ${response.message || ''}` });
            }
        } catch (err) {
            this.setState({ duettSending: false, duettError: `Duett error: ${err.message || err}` });
        }
    }

    onClickDuettCreditNote = async e => {
        const { newSale } = this.props;
        const { duettCreditConfirm } = this.state;
        if (!duettCreditConfirm) {
            this.setState({ duettCreditConfirm: true });
            if (this.duettCreditConfirmTimer) {
                clearTimeout(this.duettCreditConfirmTimer);
            }
            this.duettCreditConfirmTimer = setTimeout(() => {
                this.setState({ duettCreditConfirm: false });
            }, 5000);
            return;
        }
        if (this.duettCreditConfirmTimer) {
            clearTimeout(this.duettCreditConfirmTimer);
        }
        this.setState({
            duettCreditConfirm: false,
            duettCreditSending: true,
            duettCreditSent: false,
            duettCreditError: null,
        });
        try {
            const response = await util.fetchApi(`/api/integrations/duett/credit-notes/${newSale.id}`, {
                method: 'POST',
            }, {});
            if (response.status === 200) {
                this.setState({ duettCreditSending: false, duettCreditSent: true });
            } else {
                this.setState({ duettCreditSending: false, duettCreditError: `Duett kreditering feilet: ${response.status} - ${response.message || ''}` });
            }
        } catch (err) {
            this.setState({ duettCreditSending: false, duettCreditError: `Duett kreditering feil: ${err.message || err}` });
        }
    }

    onClickEmailPriceoffer = async e => {
        const { newSale, callback = () => {} } = this.props;
        const { to, subject, body, includeJournal } = this.state;
        this.setState({
            invoiceSent: false,
            invoiceSending: true,
        });
        const { saleStore } = this.props.stores;
        const response = await saleStore.emailPriceoffer({ sale: newSale, to, subject, body, includeJournal });
        callback(response);

        this.setState({
            invoiceSent: true,
            invoiceSending: false,
        });
    }

    componentWillUnmount = () => {
        if (this.duettCreditConfirmTimer) {
            clearTimeout(this.duettCreditConfirmTimer);
        }
        if (this.powerOfficeForceConfirmTimer) {
            clearTimeout(this.powerOfficeForceConfirmTimer);
        }
    }

    onClickPrintSale = e => {
        // console.log('onClickPrintSale', e);
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <DyrejournalSaleReceiptPrint stores={this.props.stores} />
        </IntlProvider>;

        // Render to a temporary div, extract HTML, then print via iframe
        const tempDiv = document.createElement('div');
        tempDiv.className = 'receipt';
        document.body.appendChild(tempDiv);
        render(receipt, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printViaIframe(htmlContent);
    }

    render() {
        const { smsMessage, smsSending, smsSent, smsError } = this.state;
        const { invoiceSent, invoiceSending, invoiceError, fikenSending, fikenSent, fikenError, tripletexSending, tripletexSent, tripletexError, powerOfficeSending, powerOfficeSent, powerOfficeError, powerOfficeMessage, powerOfficeRetryable, powerOfficeForceConfirm, duettSending, duettSent, duettError, duettSendOption, duettCreditSending, duettCreditSent, duettCreditError, duettCreditConfirm, invoiceReferenceSections } = this.state;
        const {
            newSale,
        } = this.props;
        const { appState, userStore, animalStore, saleStore } = this.props.stores;
        const { user, isAdmin, customer } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { visitors } = newSale;

        const {
            currentPayments = [],
        } = saleStore;

        const dueDate = new Date(newSale.dueDate || newSale.saleDate || new Date());
        if (customer.invoiceDueDays > 0 && !newSale.dueDate) {
            dueDate.setDate(dueDate.getDate() + customer.invoiceDueDays);
        }

        const { days: daysOverDue } = util.dateDiff(dueDate, new Date());
        let subject;
        if (newSale.status === 1001) {
            subject = `Pristilbud for salg #${newSale.saleNumber || newSale.id} - ${util.isoDate(newSale.saleDate)}`;
        } else if (newSale.paidDate) {
            subject = `Kvittering for salg #${newSale.saleNumber || newSale.id} - ${util.isoDate(newSale.saleDate)}`;
        } else {
            subject = `${showReminderLabel({ daysOverDue, reminders: newSale.reminders }) ? 'Påminnelse - ' : ''}Faktura for salg #${newSale.saleNumber || newSale.id} - ${util.isoDate(newSale.saleDate)}`;
        }

        const restPayment = saleStore.sumRestPayments(currentPayments, saleStore.sumTotal(newSale.products, false), false, false);

        // SMS with pay link: only for unpaid invoice sales with something left to pay and a
        // registered cellphone (the backend route refuses everything else), and under the same
        // accounting gate as the e-mail button.
        const smsCellphone = visitors?.[0]?.cellphone;
        const canSendSms = !customer.featureFlags?.preventEmailInvoice
            && !newSale.paidDate
            && SMS_INVOICE_STATUSES.includes(newSale.status)
            && restPayment > 0.5
            && !!smsCellphone;
        const smsDefaultMessage = canSendSms ? this.getDefaultSmsMessage() : '';

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    <div class='font-weight-lighter px-3 box-header'>
                        {newSale.status === 1001 ? <>
                            <Text id='dyreinvoice.priceoffer-send'>Send Price offer</Text>
                        </> : <>
                            {newSale.paidDate ? <>
                                <Text id='dyreinvoice.receipt-send'>Send Receipt</Text>
                            </> : <>
                                <Text id='dyreinvoice.invoice-send'>Send Invoice</Text>
                            </>}
                        </>}
                    </div>

                    <div class={`w-100 px-3 py-2 mt-2 d-flex flex-column`}>
                        <div class='form'>
                            <label class='sr-only'>Email</label>
                            <div class='input-group mb-2 mr-sm-2'>
                                <div class='input-group-text'>
                                    <div class='input-group-text rounded-pill-left'>@</div>
                                </div>
                                <input
                                    type='text'
                                    class='form-control rounded-pill-right'
                                    placeholder={visitors.map(v => (newSale.paidDate || newSale.status === 1001 ? v.email : (v.invoiceEmail || v.email)))}
                                    onInput={linkstate(this, 'to')}
                                />
                            </div>
                        </div>
                        <div class='form'>
                            <label class='sr-only'>Subject</label>
                            <div class='input-group mb-2 mr-sm-2'>
                                <div class='input-group-text'>
                                    <div class='input-group-text rounded-pill-left'>S</div>
                                </div>
                                <input type='text' class='form-control rounded-pill-right' placeholder={subject} onInput={linkstate(this, 'subject')} />
                            </div>
                        </div>
                        <div class='form'>
                            <label class='sr-only'>Body</label>
                            <div class='input-group mb-2 mr-sm-2'>
                                <div class='input-group-text'>
                                    <div class='input-group-text rounded-pill-left'>B</div>
                                </div>
                                <textarea type='text' class='form-control rounded-pill-right' placeholder='Body' onInput={linkstate(this, 'body')} />
                            </div>
                        </div>

                        {newSale.journals && newSale.journals.length > 0 && newSale.status !== 1001 ? <>
                            <div class='form-check mb-2 mr-sm-2'>
                                <input
                                    id='includeJournal'
                                    class='form-check-input'
                                    type='checkbox'
                                    checked={this.state.includeJournal}
                                    onInput={linkstate(this, 'includeJournal')}
                                />
                                <label class='form-check-label' for='includeJournal'>
                                    <Text id='dyreinvoice.invoice-send-include-journal'>Include journal</Text>
                                    <small class='text-body-secondary ms-1'>
                                        ({newSale.journals.map(j => `#${j.id}`).join(', ')})
                                    </small>
                                </label>
                            </div>
                        </> : ''}

                        {canSendSms ? <>
                            <div class='border rounded bg-body-secondary mt-2 mb-2 p-3'>
                                <div class='fw-bold mb-2'>
                                    <i class='fa-duotone fa-comment-sms me-1' /> <Text id='dyreinvoice.sms-invoice-title'>Send invoice by SMS</Text>
                                </div>
                                <div class='text-body-secondary small mb-2'>
                                    <Text id='dyreinvoice.sms-invoice-help'>The customer gets a text message with a personal payment link. Name, amount and payment link are filled in automatically when sending.</Text>
                                </div>
                                <div class='form'>
                                    <label class='sr-only'>Cellphone</label>
                                    <div class='input-group mb-2 mr-sm-2'>
                                        <div class='input-group-text'>
                                            <div class='input-group-text rounded-pill-left'><i class='fa-duotone fa-mobile' /></div>
                                        </div>
                                        <input type='text' class='form-control rounded-pill-right' value={smsCellphone} readOnly />
                                    </div>
                                </div>
                                <div class='form'>
                                    <label class='sr-only'>SMS</label>
                                    <div class='input-group mb-2 mr-sm-2'>
                                        <div class='input-group-text'>
                                            <div class='input-group-text rounded-pill-left'>SMS</div>
                                        </div>
                                        <textarea
                                            class='form-control rounded-pill-right'
                                            rows='3'
                                            placeholder={smsDefaultMessage}
                                            value={smsMessage}
                                            onInput={linkstate(this, 'smsMessage')}
                                        />
                                    </div>
                                </div>
                                <div class='w-100 d-flex flex-row justify-content-center'>
                                    <button
                                        type='button'
                                        class={`btn ${smsSent ? 'btn-success' : 'btn-outline-primary'} mb-2 rounded-pill`}
                                        onClick={this.onClickSmsInvoice}
                                        disabled={smsSending}
                                    >
                                        {smsSending ? <>
                                            <i class='fa-solid fa-spinner-third fa-spin' /> <Text id='dyreinvoice.sms-invoice-sending'>Sending SMS...</Text>
                                        </> : <>
                                            {smsSent ? <>
                                                <i class='fa-solid fa-check' /> <Text id='dyreinvoice.sms-invoice-sent'>SMS sent</Text>
                                            </> : <>
                                                <i class='fa-solid fa-comment-sms' /> <Text id='dyreinvoice.sms-invoice-send'>Send SMS</Text>
                                            </>}
                                        </>}
                                    </button>
                                </div>
                                {smsError ? <div class='text-danger small text-center'>{smsError}</div> : ''}
                            </div>
                        </> : ''}

                        {(customer.fikenSlug || customer.featureFlags?.fiken) && !newSale.fikenLocation && newSale.status !== 1001 ? <>
                            <div class='border rounded bg-body-secondary mt-2 mb-2 pb-3'>
                                <FormFields
                                    stores={this.props.stores}
                                    sections={invoiceReferenceSections}
                                    holdingStore={saleStore}
                                    fields={['invoiceReferenceNumber', 'invoiceReferencePerson']}
                                    objectName={'newSale'}
                                    isNew={false}
                                    hideAdmin={true}
                                    drawerLevel={this.props.drawerLevel}
                                />
                            </div>
                        </> : ''}

                        <div class='w-100 d-flex flex-row flex-wrap justify-content-center mt-3'>
                            {(!customer.featureFlags?.preventEmailInvoice || newSale.paidDate || newSale.status === 1001) && <button
                                type='button'
                                class={`btn ${invoiceSent ? 'btn-success' : 'btn-primary'} mb-2 rounded-pill`}
                                onClick={newSale.status === 1001
                                    ? this.onClickEmailPriceoffer
                                    : (newSale.paidDate ? this.onClickEmailReceipt : this.onClickEmailInvoice)}
                                disabled={invoiceSending}
                            >
                                {invoiceSending ? <>
                                    <i class='fa-solid fa-spinner-third fa-spin' /> <Text id='dyreinvoice.email-invoice-sending'>Sending email...</Text>
                                </> : <>
                                    {invoiceSent ? <>
                                        {newSale.status === 1001 ? <>
                                            <i class='fa-solid fa-check' /> <Text id='dyreinvoice.priceoffer-sent'>Priceoffer sent</Text>
                                        </> : <>
                                            {newSale.paidDate ? <>
                                                <i class='fa-solid fa-check' /> <Text id='dyreinvoice.receipt-sent'>Receipt sent</Text>
                                            </> : <>
                                                <i class='fa-solid fa-check' /> <Text id='dyreinvoice.invoice-sent'>Invoice sent</Text>
                                            </>}
                                        </>}
                                    </> : <>
                                        <i class='fa-solid fa-paper-plane' /> <Text id='dyreinvoice.email-invoice-to-address'>Send Email</Text>
                                    </>}
                                </>}
                            </button>}

                            {(customer.fikenSlug || customer.featureFlags?.fiken) && newSale.status !== 1001 ? <>
                                <button
                                    type='button'
                                    class={`btn ${fikenSent || newSale.fikenInvoiceSent ? 'btn-success' : 'btn-outline-info'} mb-2 ms-2 rounded-pill`}
                                    onClick={this.onClickFikenInvoice}
                                    disabled={fikenSending || fikenSent || !!newSale.fikenInvoiceSent}
                                >
                                    {fikenSending ? <>
                                        <i class='fa-solid fa-spinner-third fa-spin' /> Sender til Fiken...
                                    </> : <>
                                        {fikenSent || newSale.fikenInvoiceSent ? <>
                                            <i class='fa-solid fa-check' /> Sendt til Fiken
                                        </> : <>
                                            {newSale.fikenLocation ? <>
                                                <i class='fa-solid fa-paper-plane' /> Send faktura til kunde
                                            </> : <>
                                                <i class='fa-solid fa-file-invoice' /> Opprett og send til Fiken
                                            </>}
                                        </>}
                                    </>}
                                </button>
                            </> : ''}

                            {customer.featureFlags?.tripletex && newSale.status !== 1001 && newSale.status !== 1007 && newSale.status !== 1008 ? <>
                                {newSale.tripletexInvoice?.id ? <>
                                    {/* Already in Tripletex: mirror the Fiken button above instead of
                                        offering a send that the backend will refuse with 409 (TICKET-01597). */}
                                    <button
                                        type='button'
                                        class='btn btn-success mb-2 ms-2 rounded-pill'
                                        disabled
                                    >
                                        <i class='fa-solid fa-check' /> Sendt til Tripletex (faktura #{newSale.tripletexInvoice.invoiceNumber})
                                    </button>
                                </> : <>
                                    <button
                                        type='button'
                                        class={`btn ${tripletexSent ? 'btn-success' : 'btn-outline-info'} mb-2 ms-2 rounded-pill`}
                                        onClick={this.onClickTripletexInvoice}
                                        disabled={tripletexSending || tripletexSent}
                                    >
                                        {tripletexSending ? <>
                                            <i class='fa-solid fa-spinner-third fa-spin' /> Sender til Tripletex...
                                        </> : <>
                                            {tripletexSent ? <>
                                                <i class='fa-solid fa-check' /> Sendt til Tripletex
                                            </> : <>
                                                <i class='fa-solid fa-file-invoice-dollar' /> Send til Tripletex
                                            </>}
                                        </>}
                                    </button>
                                </>}
                            </> : ''}

                            {customer.featureFlags?.powerOffice && newSale.status !== 1001 ? <>
                                <button
                                    type='button'
                                    class={`btn ${powerOfficeSent ? 'btn-success' : 'btn-outline-info'} mb-2 ms-2 rounded-pill`}
                                    onClick={this.onClickPowerOfficeInvoice}
                                    disabled={powerOfficeSending || powerOfficeSent}
                                >
                                    {powerOfficeSending ? <>
                                        <i class='fa-solid fa-spinner-third fa-spin' /> Sender til PowerOffice...
                                    </> : <>
                                        {powerOfficeSent ? <>
                                            <i class='fa-solid fa-check' /> Sendt til PowerOffice
                                        </> : <>
                                            <i class='fa-solid fa-building-columns' /> Send til PowerOffice
                                        </>}
                                    </>}
                                </button>
                                {powerOfficeRetryable && !powerOfficeSent ? <>
                                    <button
                                        type='button'
                                        class='btn btn-outline-danger mb-2 ms-2 rounded-pill'
                                        onClick={this.onClickPowerOfficeForceRetransfer}
                                        disabled={powerOfficeSending}
                                    >
                                        {powerOfficeForceConfirm ? <>
                                            <i class='fa-solid fa-triangle-exclamation' /> Er du sikker? Klikk igjen
                                        </> : <>
                                            <i class='fa-solid fa-rotate-right' /> Overfør til PowerOffice på nytt
                                        </>}
                                    </button>
                                </> : null}
                            </> : ''}

                            {/* Only unpaid invoice sales become Duett invoices (lib/duettSaleStatus
                                isDuettInvoiceCandidate mirrors the backend 409/400 guards): a paid
                                sale reaches Duett via the dagsoppgjør voucher, never as an invoice. */}
                            {customer.featureFlags?.duett && isDuettInvoiceCandidate(newSale) && newSale.status !== 1001 && newSale.status !== 1007 && newSale.status !== 1008 ? <>
                                <button
                                    type='button'
                                    class={`btn ${duettSent ? 'btn-success' : 'btn-outline-info'} mb-2 ms-2 rounded-pill`}
                                    onClick={this.onClickDuettInvoice}
                                    disabled={duettSending || duettSent}
                                >
                                    {duettSending ? <>
                                        <i class='fa-solid fa-spinner-third fa-spin' /> Sender til Duett...
                                    </> : <>
                                        {duettSent ? <>
                                            <i class='fa-solid fa-check' /> Sendt til Duett
                                        </> : <>
                                            <i class='fa-solid fa-file-invoice-dollar' /> Send til Duett
                                        </>}
                                    </>}
                                </button>
                                <select
                                    class='form-select form-select-sm d-inline-block ms-2 mb-2'
                                    style='width: auto;'
                                    value={duettSendOption || defaultDuettSendOption(newSale.visitors?.[0])}
                                    onInput={e => this.setState({ duettSendOption: e.target.value })}
                                    disabled={duettSending || duettSent}
                                >
                                    <option value='email'>E-post</option>
                                    <option value='autoTrans'>EHF</option>
                                    <option value='print'>Print (sendes fra Duett)</option>
                                </select>
                            </> : null}

                            {customer.featureFlags?.duett && newSale.duettOrder?.orderId && !newSale.duettCreditOrder ? <>
                                <button
                                    type='button'
                                    class={`btn ${duettCreditSent ? 'btn-success' : 'btn-outline-danger'} mb-2 ms-2 rounded-pill`}
                                    onClick={this.onClickDuettCreditNote}
                                    disabled={duettCreditSending || duettCreditSent}
                                >
                                    {duettCreditSending ? <>
                                        <i class='fa-solid fa-spinner-third fa-spin' /> Krediterer i Duett...
                                    </> : <>
                                        {duettCreditSent ? <>
                                            <i class='fa-solid fa-check' /> Kreditert i Duett
                                        </> : <>
                                            {duettCreditConfirm ? <>
                                                <i class='fa-solid fa-triangle-exclamation' /> Er du sikker? Klikk igjen
                                            </> : <>
                                                <i class='fa-solid fa-rotate-left' /> Krediter i Duett
                                            </>}
                                        </>}
                                    </>}
                                </button>
                            </> : null}

                        </div>
                        <div class='w-100 d-flex flex-column align-items-center'>
                            {invoiceError ? <div class='text-danger mt-1 small'>{invoiceError}</div> : ''}
                            {fikenError ? <div class='text-danger mt-1 small'>{fikenError}</div> : ''}
                            {tripletexError ? <div class='text-danger mt-1 small'>{tripletexError}</div> : ''}
                            {powerOfficeError ? <div class='text-danger mt-1 small'>{powerOfficeError}</div> : ''}
                            {powerOfficeMessage && !powerOfficeError ? <div class='text-body-secondary mt-1 small'>{powerOfficeMessage}</div> : ''}
                            {powerOfficeRetryable && !powerOfficeSent ? <div class='text-body-secondary mt-1 small'>
                                Salget er allerede overført til PowerOffice. Har du slettet ordren/utkastet i PowerOffice,
                                kan du bruke "Overfør til PowerOffice på nytt". Er den ikke slettet, kan ny overføring gi duplikat.
                            </div> : ''}
                            {duettError ? <div class='text-danger mt-1 small'>{duettError}</div> : ''}
                            {duettCreditError ? <div class='text-danger mt-1 small'>{duettCreditError}</div> : ''}
                        </div>


                    </div>

                    <div class='font-weight-lighter px-3 box-header'>
                        {newSale.status === 1001 ? <>
                            <Text id='dyreinvoice.priceoffer-preview'>Priceoffer preview</Text>
                        </> : <>
                            {newSale.paidDate ? <>
                                <Text id='dyreinvoice.receipt-preview'>Receipt preview</Text>
                            </> : <>
                                <Text id='dyreinvoice.invoice-preview'>Invoice preview</Text>
                            </>}
                        </>}

                    </div>
                    <div class={`d-flex flex-column mx-3 px-5 py-5 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <div class={`d-flex flex-column px-5 py-0 box-container`}>
                            <DyrejournalSaleReceipt stores={this.props.stores} classnameContainer='inlineReceipt' />
                        </div>
                    </div>

                </div>
            </div>
        );
    }
}

export default SendInvoice;
